/* eslint-disable jsdoc/require-param */
/* eslint-disable linebreak-style */
// @flow
import { getProjection } from "../projection";

type CRSOptions = {
    /**
     * 裁图原点. 例如[-180,90]
     */
    origin: Array<Number>,
    /**
     * 数据范围. 例如[-180, -90, 180, 90]
     */
    bounds: Array<Number>,
    /**
     * 坐标系单位.
     */
    unit: "degree" | "meter",
    /**
     * dpi.表示在每英寸长度上可以打印或显示的点数，这些点可以是墨点、像素或其他形式的显示元素
     */
    dpi: 96,
    /**
     * 瓦片大小
     */
    tileSize: 256,
    /**
     * 分辨率对象,例如0级对应的分辨率 {0: 13758}。不传时则会按照给定范围bounds计算分辨率。
     */
    resolutions: Object | null,
    /**
     * 每个瓦片层级对应的层级名称，使用该值来构建出图的URL，当该值不存在时，使用level来构建出图的URL。例如0级对应的层级名称M {0:'M'}
     */
    levelValues: Object | null,
};

class CRS {
    /**
     * @class CRS
     * @MapGISExtension
     * @classdesc crs构造函数
     * @category Base
     * @param {String} epsgCode epsg编号,当前仅支持 'EPSG:4490'、'EPSG:4326'、'EPSG:4214' 、'EPSG:4610' 、'EPSG:3857'
     * @param {CRSOptions} options crs构造参数
     * @example
     * // 自定义4547坐标系
     * import mapboxgl from '@mapgis/mapbox-gl';
     * import proj4 from 'proj4';
     * // 通过proj4构建CustomProjection
    class CustomProjection4547 extends mapboxgl.Projection {
        constructor(code, options) {
            super(code, options);
            // 定义defs
            proj4.defs(
            code,
            '+proj=tmerc +lat_0=0 +lon_0=114 +k=1 +x_0=500000 +y_0=0 +ellps=GRS80 +units=m +no_defs +type=crs',
            );
            // 构建proj对象
            this._proj = proj4(code);
        }
        project(latlng) {
            return this._proj.forward(super.project(latlng));
        }
        unproject(point) {
            return this._proj.inverse(super.unproject(point));
        }
    }
    // 通过proj4构建CustomProjection
    const resolutions = {};
    // 构建分辨率数组
    for (let i = 0; i < 10; i++) {
       resolutions[i] = 13758.333333333334 / Math.pow(2, i);
    }
    // 构建自定义CRS
    const crs = new mapboxgl.CRS('EPSG:4547_zdy', {
        resolutions: resolutions,
        origin: [471388.6716802692, 3471062.732487645],
        bounds: [
            470838.2608196704, 3317100.9350651023, 603520.9945763454,
            3471291.0164404335,
        ],
        projection: new CustomProjection4547('EPSG:4547_zdy', {}),
    });
    // 构造地图options
    const options = {
        "container": "map",
        "crs": crs
        "renderWorldCopies": true,
        "center": [
            114,
            30
        ],
    }
    // 构造地图对象
    const map = new mapboxgl.Map(options);
     *
     * @return {CRS} 空间参考系对象
     */
    constructor(epsgCode: String, options: CRSOptions) {
        this._parseParams(arguments);
        // 验证是否传入数据范围
        const bounds = this.getExtent();
        if (!bounds) {
            throw new Error("未设置坐标系范围bounds");
        }
        if (!this.origin) {
            throw new Error("未设置裁图原点");
        }
        // 初始化分辨率(解决缺省坐标系初始化缺少分辨率列表的问题)
        if (Object.keys(this._resolutions).length === 0) {
            const width = bounds[2] - bounds[0];
            const height = bounds[3] - bounds[1];
            const size = Math.max(width, height);
            for (let i = 0; i < 23; i++) {
                const scale = Math.pow(2, i);
                this._resolutions[i] = size / scale / this.tileSize;
            }
        }
        // 设置投影
        this._setProjection();
        // 获取crs定义坐标系信息，设置缓存
        this.getCoordsInfo(bounds);
    }

    /**
     * @private
     * @description: 解析构造传入参数
     * @param {*} args 构造传入参数
     * @return {*}
     */
    _parseParams(args) {
        // epsg编号
        this.epsgCode = args[0];
        // 解析options
        const opts = args[1] || {};
        // 解析传入的参数
        this._parseOptions(opts);
    }

    /**
     * @private
     * @description: 解析构造options对象
     * @param {*} opts
     * @return {*}
     */
    _parseOptions(opts) {
        // 原点
        this.origin = opts.origin;
        // dpi参数
        this.dpi = opts.dpi || 96;
        // 坐标系单位
        this.unit = opts.unit || "m";
        // 解析数据范围
        this.extent = opts.bounds;
        // 分辨率
        this._resolutions = opts.resolutions || {};
        // 数组层级对应的levelValue
        this._levelValues = opts.levelValues || {};
        // scale
        this._scale = {};
        // 瓦片的size
        this.tileSize = opts.tileSize || 256;
        // 投影对象
        this.projection = opts.projection;
    }
    /**
     * @private
     * @description:设置投影信息
     * @return {*}
     */
    _setProjection() {
        if (!this.projection) {
            this.projection = getProjection(this.epsgCode, this.getExtent(), {
                origin: this.origin,
            });
        }
    }

    /**
     * 获取投影系范围，如果投影系不满足长宽一致，会在这一步被处理成长宽一致
     * @return {Array<Number>}
     */
    getExtent(): Array<Number> {
        // 设置为正方形区域
        if (!this._rectifyExtent) {
            const origin = this.origin;
            let i;
            const levelKeys = Object.keys(this._resolutions).map((v) =>
                parseInt(v)
            );
            if (levelKeys.length > 0) {
                let level = levelKeys[0];
                let resolution = this._resolutions[level];
                while (level > 0) {
                    resolution = resolution * 2;
                    level--;
                }
                i = resolution * this.tileSize;
            } else {
                const t = this.extent[2] - origin[0],
                    e = origin[1] - this.extent[1];
                i = Math.max(t, e);
            }
            // [-180, -270, 180, 90]
            // [-180, 90]   |------|------|    [180, 90]
            //              |--lt--|--rt--|
            //              |------|------|
            //              |--lb--|--rb--|
            // [-180, -270] |------|------|    [180, -270]
            this._rectifyExtent = [
                origin[0],
                origin[1] - i,
                origin[0] + i,
                origin[1],
            ];
        }
        return this._rectifyExtent;
    }

    /**
     * 获取crs原点
     * @return {Array<Number>}
     */
    getOrigin(): Array<Number> {
        return this.origin;
    }

    /**
     * @private
     * @description: 获取epsg编码
     * @return {String}
     */
    getEpsgCode() {
        return this.epsgCode;
    }

    /**
     * 获取投影坐标系单位
     * @return {String}
     */
    getUnit(): String {
        return this.unit;
    }

    /**
     * 获取dpi，默认为96
     * @return {*}
     */
    getDpi(): Number {
        return this.dpi;
    }

    /**
     * 一地图单位对应多少m
     * @param {*} mapUnit 地图单位
     * @return {*}
     */
    getMeterPerMapUnit(mapUnit: Number): Number {
        let meters = 1;
        if (mapUnit === "m" || mapUnit === "meter") {
            meters = 1;
        } else if (
            mapUnit === "degree" ||
            mapUnit === "degrees" ||
            mapUnit === "d"
        ) {
            meters = (2 * Math.PI * 6378137) / 360;
        } else if (mapUnit === "kilometer" || mapUnit === "km") {
            meters = 0.001;
        } else if (mapUnit === "inch") {
            meters = 1 / 0.025399999918;
        } else {
            // 一英尺 = 多少m
            meters = 0.3048;
        }
        return meters;
    }

    /**
     * @private
     * @description: 获取分辨率
     * @param {*} zoom
     * @param {*} tileSize
     * @return {*}
     */
    getResolution(zoom: Number, tileSize: Number): Number {
        if (!this._resolutions[zoom]) {
            const extent = this.getExtent();
            const width = extent[2] - extent[0];
            const height = extent[3] - extent[1];
            const size = Math.max(width, height);
            const i = Math.pow(2, zoom);
            this._resolutions[zoom] = size / i / tileSize;
        }
        return this._resolutions[zoom];
    }

    /**
     * @private
     * @description: 获取比例尺
     * @param {*} zoom
     * @param {*} tileSize
     * @return {*}
     */
    getScale(zoom: Number, tileSize: Number): Number {
        if (!this._scales[zoom]) {
            const r =
                this.getDpi() *
                this.getResolution(zoom, tileSize) *
                (1 / 0.0254) *
                this.getMeterPerMapUnit(this.getUnit());
            this._scales[zoom] = 1 / r;
        }
        return this._scales[zoom];
    }

    /**
     * @private
     * @description: 获取坐标系信息(对应与mapbox transform)
     * @param bounds
     * @return {*}
     */
    getCoordsInfo(bounds) {
        // mapbox-gl-修改说明：增加coordsInfo缓存，避免每次都计算
        // 修改人：龚跃健 2024年6月19日
        if (!this.coordinateInfo) {
            // mapbox-gl-修改说明：计算实际显示的范围
            // 修改人：赵凯 2024年9月9日
            // 实际数据的浏览范围(裁剪原点和数据范围区取最大)
            const origin = this.origin;
            const minX = Math.min(this.extent[0], this.extent[2], origin[0]);
            const maxX = Math.max(this.extent[0], this.extent[2], origin[0]);
            const minY = Math.min(this.extent[1], this.extent[3], origin[1]);
            const maxY = Math.max(this.extent[1], this.extent[3], origin[1]);
            const dataBounds = [minX, minY, maxX, maxY];
            // 计算经纬度边界
            const [lng1, lat2] = this.projection.unproject([
                dataBounds[0],
                dataBounds[3],
            ]);
            const [lng2, lat1] = this.projection.unproject([
                dataBounds[2],
                dataBounds[1],
            ]);
            this.coordinateInfo = this._initCoordinateInfo(bounds, [
                lng1,
                lat1,
                lng2,
                lat2,
            ]);
        }
        return this.coordinateInfo;
    }

    /**
     * @private
     * @description: 初始化坐标系信息
     * @param {*} bounds
     * @param latlngBounds
     * @return {*}
     */
    _initCoordinateInfo(bounds, latlngBounds) {
        const width = bounds[2] - bounds[0];
        const height = bounds[3] - bounds[1];
        const centerX = (bounds[2] + bounds[0]) / 2; // 0
        const centerY = (bounds[3] + bounds[1]) / 2; // -90
        const origin = this.origin;
        const originX = this.origin[0];
        const originY = this.origin[1];

        const [lng1, lat1, lng2, lat2] = latlngBounds;
        // 设置经纬度范围
        this._rectifyLngLatExtent = latlngBounds;

        const lngMin = lng1;
        const lngMax = lng2;
        const latMin = lat1;
        const latMax = lat2;

        // [-180, -270, 180, 90]
        // [-180, 90]   |------|------|    [180, 90]
        //              |--lt--|--rt--|
        //              |------|------|
        //              |--lb--|--rb--|
        // [-180, -270] |------|------|    [180, -270]
        // 计算经度长
        const latlngWidth = lngMax - lngMin;
        const latlngHeight = latMax - latMin;
        // 经纬度中心点
        const latlngCenterX = lngMin + latlngWidth / 2;
        const latlngCenterY = latMin + latlngHeight / 2;
        // 经纬度原点
        const latlngOrigin = [lngMin, latMax];
        const latlngOriginX = lngMin;
        const latlngOriginY = latMax;

        return {
            width,
            height,
            centerX,
            centerY,
            origin,
            originX,
            originY,
            latlngBounds,
            latlngWidth,
            latlngHeight,
            latlngCenterX,
            latlngCenterY,
            latlngOrigin,
            latlngOriginX,
            latlngOriginY,
        };
    }

    /**
     * @private
     * @description: 判断是否在范围内
     * @param {*} value
     * @param {*} range
     * @return {*}
     */
    _isInRange(value, range) {
        const [min, max] = range;
        return !!(value >= min && value <= max);
    }

    /**
     * @private
     * @description 获取levelValue值
     * @param {Number} zoom
     * @return {String}
     */
    _getLevelValue(zoom) {
        if (typeof this._levelValues[zoom] !== "undefined") {
            return String(this._levelValues[zoom]);
        }
        return String(zoom);
    }

    /**
     * @private
     * @description:计算地图单位多少像素
     * @param lat
     * @param worldSize
     * @return {Number}
     */
    pixelsPerMapUnit(lat: number, worldSize: number): number {
        if (this.unit !== "m" && this.unit !== "meter") {
            return (
                worldSize / (this._rectifyExtent[3] - this._rectifyExtent[1])
            );
        }
        return (
            worldSize /
            (2 * Math.PI * 6378137 * Math.abs(Math.cos(lat * (Math.PI / 180))))
        );
    }
    /**
     * @private
     * @description: 获取tileZoom对应的像素世界范围
     * @param {Number} zoom
     * @return {Number}
     */
    getScaleFactorByTileZoom(zoom) {
        const resolution0 = this.getResolution(0, this.tileSize);
        const resolution = this.getResolution(zoom, this.tileSize);
        const scaleFactor = (resolution * Math.pow(2, zoom)) / resolution0;
        return scaleFactor;
    }

    /**
     * 获取坐标系的宽度，单位为当前坐标系单位
     * @memberof CRS
     * @type {number}
     */
    get width(): number {
        return this._rectifyExtent[2] - this._rectifyExtent[0];
    }
    /**
     * 获取坐标系的高度，单位为当前坐标系单位
     * @memberof CRS
     * @type {number}
     */
    get height(): number {
        return this._rectifyExtent[3] - this._rectifyExtent[1];
    }
    /**
     * 获取坐标系的裁图原点x
     * @memberof CRS
     * @type {number}
     */
    get originX(): number {
        return this.origin[0];
    }
    /**
     * 获取坐标系的裁图原点y
     * @memberof CRS
     * @type {number}
     */
    get originY(): number {
        return this.origin[1];
    }
    /**
     * 获取地理坐标系范围
     * @memberof CRS
     * @type { Array<number>}
     */
    get lngLatExtent(): Array<number> {
        return this._rectifyLngLatExtent;
    }

    /**
     * @private
     * @description: 是否是标准二次幂划分的网格
     * @return {Boolean}
     */
    get isSquareResolution(): Boolean {
        const keys = Object.keys(this._resolutions);
        if (keys.length <= 1) return true;
        for (let i = 1; i < keys.length; i++) {
            const value = this._resolutions[keys[i - 1]];
            const value1 = this._resolutions[keys[i]];
            if (Math.abs(value - value1 * 2) > 10e-8) {
                return false;
            }
        }
        return true;
    }
}

CRS.get = function (code) {
    for (const e in CRS) {
        if (
            CRS.hasOwnProperty(e) &&
            CRS[e].getEpsgCode &&
            CRS[e].getEpsgCode() === code
        ) {
            return CRS[e];
        }
    }
    return null;
};

CRS.set = function (crs) {
    const e = crs.getEpsgCode().replace(":", "").toUpperCase();
    CRS[e] = crs;
};

/**
 * "EPSG:4326"空间参考系对象，包含默认的裁图方式。
 * @type {CRS}
 */
CRS.EPSG4326 = new CRS("EPSG:4326", {
    origin: [-180, 90],
    bounds: [-180, -90, 180, 90],
    unit: "degree",
});
/**
 * @type {CRS}
 * "EPSG:4214"空间参考系对象，包含默认的裁图方式。
 */
CRS.EPSG4214 = new CRS("EPSG:4214", {
    origin: [-180, 90],
    bounds: [-180, -90, 180, 90],
    unit: "degree",
});
/**
 * @type {CRS}
 * "EPSG:4490"空间参考系对象，包含默认的裁图方式。
 */
CRS.EPSG4490 = new CRS("EPSG:4490", {
    origin: [-180, 90],
    bounds: [-180, -90, 180, 90],
    unit: "degree",
});
/**
 * @type {CRS}
 * "EPSG:4610"空间参考系对象，包含默认的裁图方式。
 */
CRS.EPSG4610 = new CRS("EPSG:4610", {
    origin: [-180, 90],
    bounds: [-180, -90, 180, 90],
    unit: "degree",
});
/**
 * @type {CRS}
 * "EPSG:3857"空间参考系对象，包含默认的裁图方式。Map上crs未定义时，默认为此值。
 */
CRS.EPSG3857 = new CRS("EPSG:3857", {
    origin: [-20037508.3427892, 20037508.3427892],
    bounds: [
        -20037508.3427892, -20037508.3427892, 20037508.3427892,
        20037508.3427892,
    ],
});
// mapbox-gl-修改说明：增加根据外部传入的CRS定义构造CRS的方法
// 修改人：赵凯 2024年7月16日
/**
 * 增加根据外部传入的CRS定义构造CRS
 * @param {Object|String|undefined} crsDefinition CRS定义
 * @return {CRS|null}
 */
CRS.createCRS = function (
    crsDefinition: Object | String | undefined
): CRS | null {
    let crs = null;
    if (!crsDefinition) return crs;
    if (typeof crsDefinition == "object") {
        crs = crsDefinition;
    } else if (typeof crsDefinition === "string") {
        const definedCRS = CRS.get(crsDefinition);
        if (definedCRS) {
            crs = CRS.get(crsDefinition);
        } else {
            throw new Error(`crs ${crsDefinition} is not define`);
        }
    }
    return crs;
};

export default CRS;
